// src/js/home/crosshair-sync.ts
// Synchronized crosshair hover across all home dashboard datetime charts.
// Uses a local syncCharts array (NOT global Pointer.prototype.reset override)
// to avoid affecting pie charts (chart_source, chart_source_language).

import * as Highcharts from 'highcharts';

const syncCharts: Highcharts.Chart[] = [];
let syncEnabled = true;
let dateLabel: HTMLDivElement | null = null;

export function registerSyncChart(chart: Highcharts.Chart): void {
    // D-04: prevent duplicate chart entries when re-called on chart.events.render.
    if (syncCharts.includes(chart)) {
        // Chart already registered — pointer.reset noop already applied. No-op.
        return;
    }
    syncCharts.push(chart);
    const pointer = chart.pointer as any;
    if (!pointer || typeof pointer.reset !== 'function') {
        console.error('[crosshair-sync] pointer.reset not found — crosshair sync will not suppress tooltip flicker. Re-verify after Highcharts upgrade.');
        return;
    }
    // D-04 idempotency: tagged-property sentinel mirrors wireNullAwareHover idiom.
    if (pointer._crosshairSyncPatched) return;
    pointer.reset = function (): void { return undefined; };
    pointer._crosshairSyncPatched = true;
}

export function setSyncEnabled(enabled: boolean): void {
    syncEnabled = enabled;
    if (!enabled) {
        clearAllSync();
    }
}

export function clearAllSync(): void {
    syncCharts.forEach(chart => {
        chart.xAxis[0].hideCrosshair();
        chart.tooltip.hide();
        chart.series.forEach(s => {
            s.points?.forEach(p => (p as any).setState?.(''));
        });
        (chart as any).hoverPoint = null;
    });
    if (dateLabel) {
        dateLabel.style.display = 'none';
    }
}

export function initCrosshairSync(): void {
    const container = document.querySelector('.col-charts') as HTMLElement;
    if (!container) return;

    // Create floating date label for crosshair
    dateLabel = document.createElement('div');
    dateLabel.style.cssText = [
        'position: absolute',
        'pointer-events: none',
        'z-index: 10',
        'font-size: 11px',
        'color: #aaa',
        'padding: 2px 6px',
        'border-radius: 3px',
        'background: rgba(30,30,30,0.75)',
        'display: none',
        'white-space: nowrap',
    ].join(';');
    container.appendChild(dateLabel);

    container.addEventListener('mousemove', (e: MouseEvent) => {
        if (!syncEnabled) return;

        // Find ANY datetime chart under cursor (not just syncCharts)
        let primaryChart: Highcharts.Chart | null = null;
        for (const chart of (Highcharts.charts as (Highcharts.Chart | undefined)[])) {
            if (!chart || chart.xAxis[0]?.options?.type !== 'datetime') continue;
            const rect = chart.container.getBoundingClientRect();
            if (e.clientX >= rect.left && e.clientX <= rect.right &&
                e.clientY >= rect.top && e.clientY <= rect.bottom) {
                primaryChart = chart;
                break;
            }
        }
        if (!primaryChart) return;

        // Prefer hoverPoint; fall back to cursor position for empty bar dates
        const hoverPoint = (primaryChart as any).hoverPoint as Highcharts.Point | undefined;
        let hoveredX: number;
        if (hoverPoint) {
            hoveredX = hoverPoint.x;
        } else {
            const chartRect = primaryChart.container.getBoundingClientRect();
            const chartX = e.clientX - chartRect.left;
            hoveredX = primaryChart.xAxis[0].toValue(chartX);
            // Snap to nearest day, aligned to pointStart (local midnight, not UTC)
            const dayMs = 86400000;
            const pointStart = (primaryChart.series[0]?.options as any)?.pointStart as number || 0;
            const offset = pointStart % dayMs;
            hoveredX = Math.round((hoveredX - offset) / dayMs) * dayMs + offset;
            // Draw crosshair manually at the snapped position
            primaryChart.xAxis[0].drawCrosshair(undefined as any, { plotX: primaryChart.xAxis[0].toPixels(hoveredX, true) } as any);
        }

        // Format date label — include year only when data spans multiple years
        const firstSeries = primaryChart.series.find(s => s.visible && s.data.length > 0);
        let dateStr = Highcharts.dateFormat('%a, %b %e', hoveredX);
        if (firstSeries && firstSeries.data.length > 0) {
            const firstPoint = firstSeries.data[0];
            const lastPoint = firstSeries.data[firstSeries.data.length - 1];
            if (firstPoint && lastPoint) {
                const firstYear = new Date(firstPoint.x).getFullYear();
                const lastYear = new Date(lastPoint.x).getFullYear();
                if (firstYear !== lastYear) {
                    dateStr += Highcharts.dateFormat(', %Y', hoveredX);
                }
            }
        }

        // Position date label at top of primary chart plot area
        if (dateLabel) {
            const pixelX = primaryChart.xAxis[0].toPixels(hoveredX, false);
            const containerRect = container.getBoundingClientRect();
            const chartContainerEl = primaryChart.container;
            const labelLeft = chartContainerEl.offsetLeft + pixelX;
            const labelTop = chartContainerEl.offsetTop + primaryChart.plotTop - 22;
            dateLabel.textContent = dateStr;
            dateLabel.style.left = labelLeft + 'px';
            dateLabel.style.top = Math.max(0, labelTop) + 'px';
            dateLabel.style.display = 'block';
        }

        // Sync every OTHER chart to that exact date
        syncCharts.forEach(chart => {
            if (chart === primaryChart) return;

            // Collect ALL series points at this exact date (skip null gaps)
            const points = chart.series
                .filter(s => s.visible && s.data.length > 0)
                .map(s => s.data.find(p => p && p.x === hoveredX && p.y != null))
                .filter(Boolean) as Highcharts.Point[];

            if (points.length > 0) {
                // Highlight every matching point — not just [0]
                points.forEach(p => (p as any).setState?.('hover'));
                chart.tooltip.refresh(points as any);
                chart.xAxis[0].drawCrosshair(undefined as any, points[0]);
            } else {
                chart.xAxis[0].hideCrosshair();
                chart.tooltip.hide();
            }
        });
    });

    container.addEventListener('mouseleave', () => {
        clearAllSync();
    });
}
